import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { EditActions, editActions } from "../../../actions/editActions";
import { isEditActiveSelector, editedObjectIdSelector } from "../../../reducers/editReducer";
import SelectConfiguration from "./SelectConfiguration/SelectConfiguration";
import { pdApiActions, PdApiActions } from "../../../actions/pdApiActions";
import { ioConfigActions } from "../../../actions/ioConfigActions";
import AddButton from "../../../components/AddButton/AddButton";
import { getNotificationObject, hasWriteAccess } from "../../../utils/utils";
import { warning } from "react-notification-system-redux";
import IoBoardsList from "./IoBoards/IoBoardsList";
import { isEqual } from "lodash";
import { createEmptyIoBoard, createEmptyIonodePoint } from "../../../utils/pdApiUtils";
import { Dimmer, Loader } from "semantic-ui-react";
import EditIoBoard from "../../Editing/EditIoBoard";
import IoNodePointsList from "./IoNodePointsList/IoNodePointsList";
import { createEmptyIoConfigurations } from "../../../utils/pdApiUtils";
import EditIoConfigurations from "../../Editing/EditIoConfigurations";
import EditIoNodePoint from "../../Editing/EditIoNodePoint";
import EditStandardAction from "../../Editing/EditStandardAction";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import SearchEvents from "../SearchEvents/SearchEvents";
import arrowLeft from "../../../images/arrow-left-button.svg";
import rightPoint from "../../../images/ico-chevron-right.svg";
import service from "../../../images/service-24.png";
import { parseEnumStart } from "../../../utils/utils";

type StateProps = {
  pdApi: State.PdApi;
  columns: State.ColumnMap;
  mainState: State.Main;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editedObject: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  storedObject: any;
  editedObjectId: number | undefined;
  isEditActive: boolean;
  objectIdentifier: string;
  ioConfig: State.IoConfig;
  selectedIoConfiguration: string;
};

type DispatchProps = {
  editActions: typeof editActions;
  pdApiActions: typeof pdApiActions;
  ioConfigActions: typeof ioConfigActions;
  showWarning: typeof warning;
};

type OwnProps = {};
type ViewEditors =
  | "ioBoards"
  | "ioNodes"
  | "ionodePointActions"
  | "ionodePoints"
  | "ioConfigurations"
  | undefined;

export type IoConfigProps = StateProps & DispatchProps & OwnProps;
const IoConfigView = (props: IoConfigProps) => {
  const [isModalOpen, SetModalOpen] = React.useState<boolean>(false);
  const [editor, setEditor] = React.useState<ViewEditors>(undefined);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [results, setResults] = React.useState<any[] | null>(null);
  const [searchText, setSearchText] = React.useState("");
  const [currentResultIndex, setCurrentResultIndex] = React.useState(0);
  const [scrollToPointId, setScrollToPointId] = React.useState<string | undefined>(undefined);
  const [currentBoard, setCurrentBoard] = useState<string | undefined>(undefined);
  const [currentRevision, setCurrentRevision] = useState<string | undefined>(undefined);
  const [currentIoIndex, setCurrentIoIndex] = React.useState<string | undefined>(undefined);
  const [currentIoSide, setCurrentIoSide] = React.useState<string | undefined>(undefined);
  const [currentNodeCPU, setCurrentNodeCPU] = React.useState<string | undefined>(undefined);
  const [currentNodeNetwork, setCurrentNodeNetwork] = React.useState<string | undefined>(undefined);  
  const [currentNodePoint, setCurrentNodePoint] = React.useState<string | undefined>(undefined);
  const { pdApi, mainState, isEditActive, ioConfig, editedObject, storedObject } = props;

  const ioBoardsGrouped: Api.IoBoardsGroupedItem[] = ioConfig.ioBoardsGrouped;

  const hasEdited:() => boolean = () => (editedObject && !isEqual(editedObject, storedObject));
  const { activeUser } = mainState;
  const selectedIoConfiguration = ioConfig.ioConfigurations?.find(
    config => config.id === ioConfig.selectedIoConfiguration
  );

  const isActionInput = (parentPointId: string): boolean =>
    !!ioConfig.ionodePoints.find((point: Api.IoNodePoint) => point.id === parentPointId)?.isInput;

  const getStandardAction = (actionId: string, pointId: string) => {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const standardAction = ioConfig.ionodePoints
      ?.find(point => point.id === pointId)
      ?.ionodePointActions.find(action => action.id === actionId);

    return standardAction ?? null;
  };

  const clearSearchResults = () => {
    /// clear search results when search input is changed to hide Jump to next arrow buttons and not have unsynchronized ui elements
    if (results && results.length > 0) {
      setResults(null);
      setCurrentResultIndex(0);
    }
  };
  // onClick handlers
  const clearActionSelection = () => {
    props.ioConfigActions.setSelectedIoActionId("");
    props.editActions.cancelEditing();
    setEditor(undefined);
  };
  
  const handleBackClick = () => {
    setCurrentBoard(undefined);
    setCurrentRevision(undefined);
    setCurrentIoIndex(undefined);
    setCurrentIoSide(undefined);
    setCurrentNodeCPU(undefined);
    setCurrentNodeNetwork(undefined);
    setCurrentNodePoint(undefined);
    props.ioConfigActions.setSelectedIoBoardId("");
    props.ioConfigActions.setSelectedIoNodeId("");
    props.ioConfigActions.setSelectedIoPointId("");
    clearActionSelection();
  };

  const handleActionClick = (actionId: string | undefined, pointId: string) => {
    if (!actionId) return null;
    if (hasEdited()) {
      props.showWarning(
        getNotificationObject("warning", "Cannot select new entry when another has been edited.", { autoDismiss: 10 })
      );
      return null;
    }
    props.ioConfigActions.setSelectedIoActionId(actionId);
    props.ioConfigActions.setSelectedIoPointId(pointId);
    setEditor("ionodePointActions");
    const selectedStandardAction = getStandardAction(actionId, pointId);
    if (selectedStandardAction) {
      props.editActions.select(selectedStandardAction);
      const nodePoint = ioConfig.ionodePoints.find(item => item.id === pointId);
      const node = nodePoint && ioConfig.ioBoards
        .flatMap(board => board.ionodes)
        .find(n => n.id === nodePoint.nodeId);
      const board = node && ioConfig.ioBoards.find(item => item.id === node.boardId);

      if (!!board && !!node && !!nodePoint) {
        const trimmedCPU = node.cpu.enumName?.toString().startsWith("IO_CPU_") ? node.cpu.enumName.replace("IO_CPU_", "") : node.cpu.enumName;
        const trimmedNetwork = node.network.enumName?.toString();
        setCurrentBoard(board?.standard.boardType.enumName);
        setCurrentRevision(board?.standard.revision);
        setCurrentIoIndex(board?.index.toString());
        setCurrentIoSide(board?.side.toString());
        setCurrentNodeCPU(trimmedCPU + " [" + node.cpu.enumValue + "]");
        setCurrentNodeNetwork(trimmedNetwork + " [" + node.network.enumValue + "]");
        setCurrentNodePoint(nodePoint.isInput ? "Input_Index " + nodePoint.ioindex.toString() : "Output_Index " + nodePoint.ioindex.toString());
      }
    }
  };
  const handleSetSearchText = (newSearchText: string) => {
    setSearchText(newSearchText);
    if (newSearchText === "") {
      clearSearchResults();
    }
  };

  const handleBoardEditClick = (ioBoardId: string) => {//index,side edit
    if (!hasEdited()) {
      props.ioConfigActions.setSelectedIoNodeId("");
      props.ioConfigActions.setSelectedIoPointId("");
      clearActionSelection();
      const ioBoards = getBoardsFromGrouped(ioBoardsGrouped);
      const selectedIoBoard = ioBoards.find(ioBoard => ioBoard.id === ioBoardId);
      if (selectedIoBoard) {
        const idAvailable = ioConfig.ionodePoints.filter(
          item => item.boardId === selectedIoBoard.id
        );
        if (idAvailable.length === 0) {
          selectedIoBoard.ionodes.forEach((item:Api.IoNode) => {
            props.pdApiActions.fetchSingle("ionodes", item.id);
          });
        }
        props.editActions.select(selectedIoBoard);
        if (selectedIoBoard.id !== ioConfig.selectedIoBoard.ioBoardId) {
          props.ioConfigActions.setSelectedIoBoardId(ioBoardId);
        }
      }
      setEditor("ioBoards");
      setCurrentBoard(selectedIoBoard?.standard.boardType.enumName);
      setCurrentRevision(selectedIoBoard?.standard.revision);
      setCurrentIoIndex(selectedIoBoard?.index.toString());
      setCurrentIoSide(selectedIoBoard?.side.toString());
      setCurrentNodeCPU(undefined);
      setCurrentNodeNetwork(undefined);
      setCurrentNodePoint(undefined);
    } else {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
  };

  const getTargetAction = (
    standardActions: Api.StandardAction[],
    newSearchText: string
  ): Api.StandardAction[] =>
    standardActions.filter(
      action =>
        action.eventClassEnumNavigation.enumName.includes(newSearchText.toUpperCase()) ||
        action.eventTypeEnumNavigation.enumName.includes(newSearchText.toUpperCase()) ||
        newSearchText.toUpperCase().includes(action.eventClassEnumNavigation.enumName + " [" + action.eventClassEnumNavigation.enumValue + "]") ||
        newSearchText.toUpperCase().includes(action.eventTypeEnumNavigation.enumName+ " [" + action.eventTypeEnumNavigation.enumValue + "]")
    );

  // Search function handlers are here instead of searchEvents component
  // because we want to scroll to next result in child component using refs
  const handleOnSearchSubmitClick = (newSearchText: string, nodeId: string | undefined) => {
    const currentNodeId = nodeId;
    if (currentNodeId) {
      const points = ioConfig.ionodePoints.filter(
        (point: Api.IoNodePoint) => point.nodeId === currentNodeId
      );

      const pointsWithActions = points.filter(point => !!point.ionodePointActions);
      const resultList = pointsWithActions.reduce((_resultList: Internal.IOSearchItem[], point) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const targetActions = getTargetAction(point.ionodePointActions!, newSearchText);
        if (targetActions.length) {
          const results: Internal.IOSearchItem[] = targetActions.map(result => {
            const item: Internal.IOSearchItem = {
              id: result.id,
              parentPointId: result.parentPointId,
              index: point.ioindex,
              isInput: point.isInput ? 1 : 0
            };
            return item;
          });
          return [..._resultList, ...results];
        }
        return _resultList;
      }, []);
      /// So we can jump to next result in same order than they are displayed
      const resultsSortedByIndex = resultList.sort((a, b) => {
        if (a.isInput === b.isInput) {
          return a.index - b.index;
        } else {
          return b.isInput - a.isInput;
        }
      });
      setResults(resultsSortedByIndex);
      if (resultsSortedByIndex.length > 0) {
        setScrollToPointId(resultsSortedByIndex[0].parentPointId);
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        handleActionClick(resultsSortedByIndex[0].id!, resultsSortedByIndex[0].parentPointId!);
      }
    } else {
      handleSearchNextNodeClick(searchText, true);
    }
  };
  const handleNextResultClick = (direction: "up" | "down") => {
    if (results && results.length > 0) {
      /// if nextIndex goes over the lenght of results go back to zero, or end of list in reverse
      const nextIndex =
        direction === "down"
          ? currentResultIndex + 1 >= results.length
            ? 0
            : currentResultIndex + 1
          : currentResultIndex - 1 < 0
            ? results.length - 1
            : currentResultIndex - 1;
      setCurrentResultIndex(nextIndex);
      setScrollToPointId(results[nextIndex].parentPointId);
      handleActionClick(results[nextIndex].id, results[nextIndex].parentPointId);
    }
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const getBoardsFromGrouped = (groupedBoards: any[]) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const ioBoardsList: any[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.keys(groupedBoards).forEach((key: any) => {
      const boardType = groupedBoards[key];
      Object.keys(boardType).forEach((revisionName: string) => {
        const revisionBoards = boardType[revisionName];
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        revisionBoards.map((b: any) => ioBoardsList.push(b));
      });
    });
    return ioBoardsList;
  };

  const handleSearchNextNodeClick = (searchTerm: string, getFirstNode?: boolean) => {
    const ioBoardsList = getBoardsFromGrouped(ioConfig.ioBoardsGrouped);
    let nextNodesParentRevisionId = "";
    let nextNodeId = "";
    let nextBoardTypeName = "";
    let getNextNodeIdFlag = getFirstNode;

    ioBoardsList.forEach((iob: Api.IoBoard) => {
      iob.ionodes.forEach((node: Api.IoNode) => {
        if (getNextNodeIdFlag) {
          nextNodesParentRevisionId = iob.standard.id;
          nextNodeId = node.id;
          nextBoardTypeName = iob.standard.boardType.enumName;
          getNextNodeIdFlag = false;
          return;
        }
        if (node.id === ioConfig.selectedIoBoard.ioNodeId) {
          getNextNodeIdFlag = true;
        }
      });
    });
    props.pdApiActions.fetchSingle("ionodes", nextNodeId);
    props.ioConfigActions.setSelectedIoNodeId(nextNodeId);
    if (
      nextNodesParentRevisionId !== "" &&
      !ioConfig.openedRows.ioRevisions.includes(nextNodesParentRevisionId)
    ) {
      props.ioConfigActions.updateOpenedIoRevisions(nextNodesParentRevisionId);
    }
    if (nextBoardTypeName !== "" && !ioConfig.openedRows.ioBoardTypes.includes(nextBoardTypeName)) {
      props.ioConfigActions.updateOpenedIoBoardTypes(nextBoardTypeName);
    }

    handleOnSearchSubmitClick(searchTerm, nextNodeId);
  };

  const handleAddBoardClick = () => {
    if (!hasEdited()) {
      handleBackClick();
      setEditor("ioBoards");
      props.editActions.startEditing(
        createEmptyIoBoard(pdApi, props.ioConfig.selectedIoConfiguration),
        true
      );
    } else {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
  };
  const handleAddIoConfgurationClick = () => {
    if (!hasEdited()) {
      setEditor("ioConfigurations");
      props.editActions.startEditing(createEmptyIoConfigurations(pdApi), true);
    }
  };

  const handleEditIoConfiguration = () => {
    if (!hasEdited()) {
      handleBackClick();
      if (props.ioConfig && props.ioConfig.ioConfigurations) {
      setEditor("ioConfigurations");
      props.editActions.select(selectedIoConfiguration);
    }
    } else {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
  };
  const handleNodeClick = (node: Api.IoNode) => {
    if (hasEdited()) {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
    props.ioConfigActions.setSelectedIoPointId("");
    clearActionSelection();
    props.ioConfigActions.setSelectedIoActionId("");
    const board = ioConfig.ioBoards.filter(item => item.id === node.boardId)[0];

    if (board) {
      const trimmedCPU = node.cpu.enumName?.toString().startsWith("IO_CPU_") ? node.cpu.enumName.replace("IO_CPU_", "") : node.cpu.enumName;
      const trimmedNetwork = node.network.enumName?.toString();
      setCurrentBoard(board?.standard.boardType.enumName);
      setCurrentRevision(board?.standard.revision);
      setCurrentIoIndex(board?.index.toString());
      setCurrentIoSide(board?.side.toString());
      setCurrentNodeCPU(trimmedCPU + " [" + node.cpu.enumValue + "]");
      setCurrentNodeNetwork(trimmedNetwork + " [" + node.network.enumValue + "]");
      setCurrentNodePoint(undefined);
    }
    if (props.ioConfig.ionodePoints.filter(point => point.nodeId === node.id).length < 1) {
      props.pdApiActions.fetchSingle("ionodes", node.id);
    }

    if (node.boardId !== ioConfig.selectedIoBoard.ioBoardId) {
      props.ioConfigActions.setSelectedIoBoardId(node.boardId);
    }
    props.ioConfigActions.setSelectedIoNodeId(node.id);
  };
  const handleRevisionClick = (id: string) => {
    if (hasEdited()) {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
    props.ioConfigActions.setSelectedIoNodeId("");
    props.ioConfigActions.setSelectedIoPointId("");
    clearActionSelection();
    props.ioConfigActions.updateOpenedIoRevisions(id);
    const board = ioConfig.ioBoards.filter(item => item.standard.id === id);
    if (board) {
      setCurrentBoard(board[0].standard.boardType.enumName);
      if(ioConfig.openedRows.ioRevisions.includes(id)){
        setCurrentRevision(undefined);
        setCurrentIoIndex(undefined);
        setCurrentIoSide(undefined);
      }else {
        setCurrentRevision(board[0].standard.revision);
        setCurrentIoIndex(board[0].index.toString());
        setCurrentIoSide(board[0].side.toString());
      }      
      setCurrentNodeCPU(undefined);
      setCurrentNodeNetwork(undefined);
      setCurrentNodePoint(undefined);
    }
  };
  const handleBoardTypeClick = (id: string) => {//onclick of board
    if (hasEdited()) {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
    props.ioConfigActions.setSelectedIoNodeId("");
    props.ioConfigActions.setSelectedIoPointId("");
    clearActionSelection();
    props.ioConfigActions.updateOpenedIoBoardTypes(id);
    if (id) {

      if (ioConfig.openedRows.ioBoardTypes.includes(id)){//to suppport open close of board types
        setCurrentBoard(undefined);
      } else {
        setCurrentBoard(id);
      }
      setCurrentRevision(undefined);
      setCurrentIoIndex(undefined);
      setCurrentIoSide(undefined);
      setCurrentNodeCPU(undefined);
      setCurrentNodeNetwork(undefined);
      setCurrentNodePoint(undefined);
    }
  };
  const handleNodeEditClick = (node: string) => {//gues not invoked not sure
    if (!(hasEdited())) {
      props.editActions.cancelEditing();
      setEditor("ioNodes");
    }
    props.editActions.select(node);
  };
  const handlePointClick = (id: string) => {//gues not invoked not sure
    clearActionSelection();
    props.ioConfigActions.setSelectedIoPointId(id);
  };
  const handlePointEditClick = (point: Api.IoNodePoint) => {
    if (hasEdited()) {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
    clearActionSelection();
    props.ioConfigActions.setSelectedIoPointId(point.id);
    setCurrentNodePoint(point?.isInput ? "Input_Index " + point?.ioindex.toString() : "Output_Index " + point?.ioindex.toString());
    if (!hasEdited()) {
      props.ioConfigActions.setSelectedIoActionId("");
      props.editActions.cancelEditing();
      setEditor("ionodePoints");
    }
    props.editActions.select(point);
  };  
  const handleAddPointClick = () => {
    if (!hasEdited()) {
      clearActionSelection();
      props.ioConfigActions.setSelectedIoPointId("");
      setCurrentNodePoint(undefined);
      const parentNodeId = ioConfig.selectedIoBoard.ioNodeId;
      if (parentNodeId) {
        setEditor("ionodePoints");
        props.editActions.startEditing(createEmptyIonodePoint(parentNodeId), true);
      }
    } else {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
  };

  const handleIoConfigurationClick = (id: string) => {
    if (props.ioConfig.selectedIoConfiguration !== id) {
      props.ioConfigActions.setSelectedIoConfigurationId(id);
    }
  };

  const handleReturnToSelectIoConfig = () => {
    if (hasEdited()) {
      props.showWarning(
        getNotificationObject("warning", "There are some unsaved changes. Please save or cancel them first!", { autoDismiss: 10 })
      );
      return null;
    }
    clearSearchResults();
    setSearchText("");
    props.ioConfigActions.setSelectedIoConfigurationId("");
    const emptyIoOpenedRows = {
      standardBoardTypes: props.ioConfig.openedRows.standardBoardTypes,
      standardBoards: props.ioConfig.openedRows.standardBoards,
      ioBoardTypes: [],
      ioBoards: [],
      ioRevisions: []
    };
    props.ioConfigActions.setOpenedRows(emptyIoOpenedRows);
    props.ioConfigActions.clearSelectedIoIds();
    handleBackClick();
  };

  /// end of click handlers

  const toggleModal = (): void => {
    SetModalOpen(!isModalOpen);
  };
  useEffect(() => {
    props.pdApiActions.fetchIoConfigurations();
    props.pdApiActions.fetchStandardBoardsShallow();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!!props.ioConfig.ioConfigurations && window.location.hash) {
      // if hash is set in url, select the ioconfig and then get rid of the hash in url
      // - this is used when notification link is pressed when user is NOT in url /tools/io-configuration/
      props.ioConfigActions.setSelectedIoConfigurationId(window.location.hash.replace("#", ""));
      history.pushState({}, "", "/tools/io-configuration/");
    }
  }, [props.ioConfig.ioConfigurations]); // eslint-disable-line

  window.onhashchange = function () {
    props.editActions.cancelEditing();

    // if hash is set in url, select the ioconfig and then get rid of the hash in url
    // - this is used when notification link is pressed when user is in url /tools/io-configuration/
    props.ioConfigActions.setSelectedIoConfigurationId(window.location.hash.replace("#", ""));
    history.pushState({}, "", "/tools/io-configuration/");
  };

  useEffect(() => {
    props.pdApiActions.fetchSingle("ioConfigurations", props.ioConfig.selectedIoConfiguration);
  }, [props.ioConfig.selectedIoConfiguration]); // eslint-disable-line

  useEffect(() => {
    if (searchText && props.ioConfig.selectedIoBoard.ioNodeId) {
      setCurrentResultIndex(0);
      handleOnSearchSubmitClick(searchText, props.ioConfig.selectedIoBoard.ioNodeId);
    }
  }, [props.ioConfig.ionodePoints, props.ioConfig.selectedIoBoard.ioNodeId]);

  useEffect(() => {
        // eslint-disable-line
  }, [currentBoard, currentRevision, currentIoIndex, currentIoSide ,currentNodeCPU, currentNodeNetwork, currentNodePoint]); // eslint-disable-line

  useEffect(() => {
      //if (!props.ioConfig.ioBoardsGrouped) return;
      //const ioBoards = (props.ioConfig.ioBoardsGrouped && props.ioConfig.selectedIoBoard?.ioBoardId) ? getBoardsFromGrouped(props.ioConfig.ioBoardsGrouped) : [];
              
    //use above or below to get ioBoards
    if (!props.ioConfig.ioBoards) return;
    const ioBoards = props.ioConfig.ioBoards;
    // Handle selectedIoBoardId
    if (props.ioConfig.selectedIoBoard?.ioBoardId && ioBoards.length > 0)
    {
      const selectedIoBoard = ioBoards.find(ioBoard => ioBoard.id === props.ioConfig.selectedIoBoard?.ioBoardId );
      if (selectedIoBoard) {
        setCurrentBoard(selectedIoBoard.standard?.boardType.enumName);
        setCurrentRevision(selectedIoBoard.standard?.revision);
        setCurrentIoIndex(selectedIoBoard.index?.toString());
        setCurrentIoSide(selectedIoBoard.side?.toString());
        setCurrentNodeCPU(undefined);
        setCurrentNodeNetwork(undefined);
        setCurrentNodePoint(undefined);
      }
    }
    // Handle selectedIoNodeId
    if (props.ioConfig.selectedIoBoard?.ioNodeId 
      && props.ioConfig.selectedIoBoard?.ioBoardId 
      && ioBoards.length > 0) {
      const selectedNode = ioBoards
        .flatMap(board => board.ionodes)
        .find(node => node.id === props.ioConfig.selectedIoBoard?.ioNodeId);    
      if (selectedNode?.boardId) {        
        const selectedBoard = ioBoards.filter(item => item.id === selectedNode.boardId)[0];
        if (selectedBoard) {
          const trimmedCPU = selectedNode.cpu.enumName?.toString().startsWith("IO_CPU_") ? selectedNode.cpu.enumName.replace("IO_CPU_", "") : selectedNode.cpu.enumName;
          const trimmedNetwork = selectedNode.network.enumName?.toString();
          setCurrentBoard(selectedBoard?.standard.boardType.enumName);
          setCurrentRevision(selectedBoard?.standard.revision);
          setCurrentIoIndex(selectedBoard?.index.toString());
          setCurrentIoSide(selectedBoard?.side.toString());
          setCurrentNodeCPU(trimmedCPU + " [" + selectedNode.cpu.enumValue + "]");
          setCurrentNodeNetwork(trimmedNetwork + " [" + selectedNode.network.enumValue + "]");
          setCurrentNodePoint(undefined);
        }
      }
    }
    // Handle selectedIoPointId  
    if (props.ioConfig.selectedIoBoard?.ioPointId 
      && props.ioConfig.selectedIoBoard?.ioNodeId 
      && props.ioConfig.selectedIoBoard?.ioBoardId 
      && props.ioConfig.ionodePoints
      && ioBoards.length > 0) {
      const selectedIONodePoint = props.ioConfig.ionodePoints.find(item => item.id === props.ioConfig.selectedIoBoard?.ioPointId);
      const selectedNode = selectedIONodePoint && props.ioConfig?.ioBoards
        .flatMap(board => board.ionodes)
        .find(n => n.id === selectedIONodePoint.nodeId);
      const selectedIOBoard = selectedNode && props.ioConfig?.ioBoards.find(item => item.id === selectedNode.boardId);
      if (!!selectedIOBoard && !!selectedNode && !!selectedIONodePoint) {
        const trimmedCPU = selectedNode.cpu.enumName?.toString().startsWith("IO_CPU_") ? selectedNode.cpu.enumName.replace("IO_CPU_", "") : selectedNode.cpu.enumName;
        const trimmedNetwork = selectedNode.network.enumName?.toString();
        setCurrentBoard(selectedIOBoard?.standard.boardType.enumName);
        setCurrentRevision(selectedIOBoard?.standard.revision);
        setCurrentIoIndex(selectedIOBoard?.index.toString());
        setCurrentIoSide(selectedIOBoard?.side.toString());
        setCurrentNodeCPU(trimmedCPU + " [" + selectedNode.cpu.enumValue + "]");
        setCurrentNodeNetwork(trimmedNetwork + " [" + selectedNode.network.enumValue + "]");
        setCurrentNodePoint(selectedIONodePoint.isInput ? "Input_Index " + selectedIONodePoint.ioindex.toString() : "Output_Index " + selectedIONodePoint.ioindex.toString());
      }
    }
  }, [props.ioConfig.ioBoards, props.ioConfig.ionodePoints]); // eslint-disable-line

  const getEditor = (editorType: ViewEditors) => {
    switch (editorType) {
      case "ioBoards":
        return <EditIoBoard {...props} toggleModal={toggleModal} />;
      case "ionodePoints":
        return <EditIoNodePoint {...props} toggleModal={toggleModal} />;
      case "ioConfigurations":
        return <EditIoConfigurations {...props} toggleModal={toggleModal} />;
      default:
        return null;
    }
  };

  const buildModal = () => {
    const onConfirmAction: Api.AnyItem = () => props.editActions.delete(editor as Api.OptionsGrud);
    const content = (
      <span>
        Are you sure you want to delete this {editor?.slice(0, -1) === "ioConfiguration"? "IO configuration" : editor?.slice(0, -1)}?
        <br />
        {editor?.slice(0, -1) !== "ioConfiguration"? "\"" + currentBoard + "\" revision \"" + currentRevision + "\"": ""}
        <br />
      </span>
    );

    return (
      <ConfirmationModal
        isOpen={isModalOpen}
        size="mini"
        header="Confirm delete"
        content={content}
        confirmText="Confirm"
        cancelText="Cancel"
        onConfirm={onConfirmAction}
        onCancel={toggleModal}
      />
    );
  };
  // 220px is the offset from top of screen. setting fixed height solved compatilibity issues with overflow scrolling
  const ioBoardsHeight = window.innerHeight - 220;

  const Editor = getEditor(editor);
  if (!props.ioConfig.selectedIoConfiguration) {
    return (
      <div className="select-configurations-view">
        <SelectConfiguration
          isLoadingConfigurations={ioConfig.localFetchingStatus === "Fetching"}
          onIoConfigurationClick={handleIoConfigurationClick}
          onAddIoConfigurationClick={handleAddIoConfgurationClick}
        />
        {isEditActive && editor === "ioConfigurations" && (
          <Dimmer active={true} page={true}>
            <div className="edit-view edit-ioConfiguration-view">{Editor}</div>
          </Dimmer>
        )}
      </div>
    );
  } else {
    return (
      <div className="io-wrapper">
        {(ioConfig.localFetchingStatus === "Updating" ||
          ioConfig.localFetchingStatus === "Adding" ||
          ioConfig.localFetchingStatus === "Fetching IO boards") && (
          <Dimmer active={true} inverted={true}>
            <Loader content={ioConfig.localFetchingStatus} />
          </Dimmer>
        )}
        <div className="io-header">
          <div className="io-header-title">
            <img
              src={arrowLeft}
              alt="Go back"
              onClick={() => handleReturnToSelectIoConfig()}
              id="goBacktoSelectConfiguration"
            />
            <h2 id="headerFriendlyName">{selectedIoConfiguration?.friendlyName} </h2>
            <img
              src={service}
              alt="Edit Selected Io Configuration"
              onClick={() => handleEditIoConfiguration()}
              id="editIoConfiguration"
            />
            {currentBoard && <span id="pathTitleIoStd">&nbsp;&nbsp;&nbsp;{parseEnumStart(currentBoard, 2)}</span>}
            {currentRevision && (
              <>
                <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
                <span id="pathTitleIoStd">Revision&nbsp;{currentRevision}</span>
              </>
            )}
            {currentIoIndex && (
              <>
                <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
                <span id="pathTitleIoStd">Index&nbsp;{currentIoIndex}</span>
              </>
            )}
            {currentIoSide && (
              <>
                <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
                <span id="pathTitleIoStd">Side&nbsp;{currentIoSide}</span>
              </>
            )} 
            {currentNodeCPU && (
              <>
                <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
                <span id="pathTitleIoStd">{currentNodeCPU}</span>
              </>
            )}
            {currentNodeNetwork && (
              <>
                <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
                <span id="pathTitleIoStd">{currentNodeNetwork}</span>
              </>
            )}
            {(currentNodePoint) && (
              <>
                <img src={rightPoint} id="pathIoStdConfiguration" alt="right point" />
                <span id="pathTitleIoStd">
                  {currentNodePoint?.split(" ")[0]}{" "}
                  {currentNodePoint?.split(" ")[1]}
                </span>
              </>
            )}
          </div>
          <div className="io-search">
            <SearchEvents
              searchText={searchText}
              setSearchText={handleSetSearchText}
              onSearchNextNodeClick={handleSearchNextNodeClick}
              currentNodeId={ioConfig.selectedIoBoard.ioNodeId}
              currentIndex={currentResultIndex}
              onSearchTextChange={clearSearchResults}
              numberOfResults={results ? results.length : null}
              onNextResultClick={handleNextResultClick}
              onSearchSubmitClick={handleOnSearchSubmitClick}
            />
          </div>
        </div>
        <div className="io-content-wrapper">
          <div className="io-boards">
            <div className="io-boards-header">
              <span>Boards</span>
              {hasWriteAccess(activeUser, "Lift") ? (
                <AddButton onClick={handleAddBoardClick} title="Add board" id="addBoardButton" />
              ) : (
                <AddButton onClick={handleAddBoardClick} title="Add board" id="disablebutton" />
              )}
            </div>

            <div style={{ height: ioBoardsHeight + "px", overflowY: "scroll" }}>
              <IoBoardsList
                rows={ioBoardsGrouped}
                onNodeSelect={handleNodeClick}
                onRevisionClick={handleRevisionClick}
                onNodeEditClick={handleNodeEditClick}
                onBoardTypeClick={handleBoardTypeClick}
                onBoardEditClick={handleBoardEditClick}
              />
            </div>
          </div>
          {ioConfig.selectedIoBoard.ioNodeId && (
            <div className="io-boards" >
                
              <IoNodePointsList
                scrollToPointId={scrollToPointId}
                loading={props.ioConfig.localFetchingStatus === "Fetching"}
                ionodePoints={props.ioConfig.ionodePoints.filter(
                  point => point.nodeId === props.ioConfig.selectedIoBoard.ioNodeId
                )}
                onActionClick={handleActionClick}
                selectedActionId={ioConfig.selectedIoBoard.ioActionId}
                onAddNewPointClick={handleAddPointClick}
                onPointClick={handlePointClick}
                onPointEditClick={handlePointEditClick}
                activeUser={activeUser}
              />
            </div>
          )}

          {isEditActive &&
            editor === "ionodePointActions" &&
            ioConfig.selectedIoBoard.ioPointId &&
            ioConfig.selectedIoBoard.ioActionId && (
              <div className="io-action-editor">
                <EditStandardAction
                  {...props}
                  toggleModal={toggleModal}
                  isInput={isActionInput(ioConfig.selectedIoBoard.ioPointId)}
                  actionType="ionodePointActions"
                />
              </div>
            )}
        </div>
        {isEditActive &&
          editor !== "ionodePointActions" &&
          editor !== "ioConfigurations" &&
          (hasEdited() ? (
            <Dimmer active={hasEdited()} page={true}>
              <div className="edit-view-hasEdited">{Editor}</div>
            </Dimmer>
          ) : (
            <Dimmer active={true} page={true}>
              <div className="edit-view">{Editor}</div>
            </Dimmer>
          ))}
        {isEditActive && editor === "ioConfigurations" && (
          <Dimmer active={true} page={true}>
            <div className="edit-view edit-ioConfiguration-view">{Editor}</div>
          </Dimmer>
        )}
        {buildModal()}
      </div>
    );
  }
};
const mapStateToProps = (state: State.Root) => ({
  columns: state.columns,
  mainState: state.main,
  pdApi: state.pdApi,
  isEditActive: isEditActiveSelector(state.editView),
  editedObjectId: editedObjectIdSelector(state.editView),
  ioConfig: state.ioConfig,
  editedObject: state.editView.editedObject,
  storedObject: state.editView.storedObject,
  selectedIoConfiguration: state.ioConfig.selectedIoConfiguration
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions | PdApiActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  pdApiActions: bindActionCreators({ ...pdApiActions }, dispatch),
  showWarning: bindActionCreators(warning, dispatch),
  ioConfigActions: bindActionCreators({ ...ioConfigActions }, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(IoConfigView);
